// src/App.js
import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';

// Import components
import ScrollToTop from './components/ScrollToTop';
import LandingPage from './pages/LandingPage';
import Login from './pages/Login';
import Register from './pages/Register';
import OrganisationDetails from './pages/OrganisationDetails';
import InstantAssessment from './pages/InstantAssessment';
import AssessmentInstructions from './pages/AssessmentInstructions';
import AssessmentQuestions from './pages/AssessmentQuestions';
import AssessmentReview from './pages/AssessmentReview';
import { AssessmentProvider } from './contexts/AssessmentContext';
import AssessmentResults from './pages/AssessmentResults';
import AdminLogin from './pages/AdminLogin';
import AdminDashboard from './pages/AdminDashboard';
import AdminReview from './pages/AdminReview';
import UserLogin from './pages/UserLogin';
import UserDashboard from "./pages/UserDashboard";

const theme = createTheme({
  palette: {
    primary: {
      main: '#00438f',
      light: '#00438f',
      dark: '#004B99',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#00B4A6',
      light: '#4DD7CB',
      dark: '#008B80',
      contrastText: '#ffffff',
    },
    background: {
      default: '#F8F9FA',
      paper: '#ffffff',
      accent: '#E8F4FD',
    },
    text: {
      primary: '#2C3E50',
      secondary: '#5A6C7D',
      disabled: '#8E9AAF',
    },
    divider: '#DEE2E6',
  },
  typography: {
    fontFamily: '"Segoe UI", "Roboto", "Helvetica Neue", "Arial", sans-serif',
    h1: { fontWeight: 700, fontSize: '2.5rem', color: '#2C3E50' },
    h2: { fontWeight: 600, fontSize: '2rem', color: '#2C3E50' },
    h3: { fontWeight: 600, fontSize: '1.75rem', color: '#2C3E50' },
    h4: { fontWeight: 600, fontSize: '1.5rem', color: '#2C3E50' },
    h5: { fontWeight: 600, fontSize: '1.25rem', color: '#2C3E50' },
    h6: { fontWeight: 600, fontSize: '1.125rem', color: '#2C3E50' },
    button: { textTransform: 'none', fontWeight: 500 },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          textTransform: 'none',
          fontWeight: 500,
        },
        containedPrimary: {
          background: 'linear-gradient(135deg, #00438f 0%, #004B99 100%)',
          boxShadow: '0 3px 12px rgba(0, 102, 204, 0.3)',
          '&:hover': {
            background: 'linear-gradient(135deg, #004B99 0%, #003366 100%)',
            boxShadow: '0 4px 16px rgba(0, 102, 204, 0.4)',
          },
        },
        containedSecondary: {
          background: 'linear-gradient(135deg, #00B4A6 0%, #008B80 100%)',
          boxShadow: '0 3px 12px rgba(0, 180, 166, 0.3)',
          '&:hover': {
            background: 'linear-gradient(135deg, #008B80 0%, #006B5D 100%)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
          border: '1px solid #E9ECEF',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 6,
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: '#00B4A6',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: '#00438f',
              borderWidth: 2,
            },
          },
          '& .MuiInputLabel-root.Mui-focused': {
            color: '#00438f',
          },
        },
      },
    },
  },
  shape: {
    borderRadius: 6,
  },
});

// Simple placeholder component
const PlaceholderPage = ({ title }) => (
  <div style={{ padding: '2rem', textAlign: 'center' }}>
    <h2>{title}</h2>
    <p>This page is coming soon...</p>
  </div>
);

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
       <AssessmentProvider>
      <Router>
          <ScrollToTop /> 
          <Routes>
            {/* Routes for components we have */}
            <Route path="/" element={<LandingPage />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/organisation-details" element={<OrganisationDetails />} />
            <Route path="/assessment/instant" element={<InstantAssessment />} />
            <Route path="/assessment/instructions" element={<AssessmentInstructions />} />
            <Route path="/assessment/questions" element={<AssessmentQuestions />} />
            <Route path="/assessment/review" element={<AssessmentReview />} />
            <Route path="/assessment/results" element={<AssessmentResults />} />
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin/dashboard" element={<AdminDashboard />} />
            <Route path="/admin/review/:assessmentId" element={<AdminReview />} />
            <Route path="/user/login" element={<UserLogin />} />            
            <Route path="/user/dashboard" element={<UserDashboard />} />

            {/* Placeholder routes */}
            <Route
              path="/survey"
              element={<PlaceholderPage title="Survey Form" />}
            />
            <Route
              path="/results"
              element={<PlaceholderPage title="Results" />}
            />
          
          {/* Catch-all route */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        
      </Router>
      </AssessmentProvider>
    </ThemeProvider>
  );
}

export default App;